import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import ReactMarkdown from "react-markdown";
import styles from "./Assessment.module.css";
import api from "../../api";
import { assessmentParam, inAssessment, withAssessment } from "./assessmentFilter";
import { AnswerReveal, LOCKED_NOTE, ReferenceText } from "./AnswerReveal";

const toolbarOptions = [
  [{ header: [1, 2, 3, false] }],
  ["bold", "italic", "underline", "strike"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["blockquote", "code-block"],
  ["link"],
  ["clean"],
];

const modules = {
  toolbar: {
    container: toolbarOptions,
  },
  clipboard: {
    matchVisual: false,
  },
};

const SubjectiveWorkspace = () => {
  const { id, topicId, problemId } = useParams();
  const assessment = assessmentParam(useLocation().search);
  const navigate = useNavigate();
  const selectedModuleId = useSelector((state) => state.module.selectedModuleId);
  const effectiveTopicId = topicId || selectedModuleId;

  // Problem state
  const [problems, setProblems] = useState([]);
  const [currentProblemIndex, setCurrentProblemIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Editor state
  const [answer, setAnswer] = useState("");
  const [isSaved, setIsSaved] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const currentProblem = problems[currentProblemIndex];

  // Fetch problems on mount
  useEffect(() => {
    const fetchProblems = async () => {
      if (!effectiveTopicId) return;

      setLoading(true);
      try {
        const response = await api.get(`course/${effectiveTopicId}/s/`);
        // Opened from one assessment: only its questions.
        const data = (response.data || []).filter((p) => inAssessment(p, assessment));
        setProblems(data);

        if (data.length > 0) {
          // If problemId is provided, find and select it
          if (problemId) {
            const index = data.findIndex(p => p.id === parseInt(problemId));
            if (index >= 0) setCurrentProblemIndex(index);
          }

          // Load first problem's answer
          const problem = problemId
            ? data.find(p => p.id === parseInt(problemId)) || data[0]
            : data[0];
          if (problem.submission_data?.[0]?.submission) {
            setAnswer(problem.submission_data[0].submission);
          } else {
            setAnswer("");
          }
        }
      } catch (err) {
        setError(err.message || "Failed to load questions");
      } finally {
        setLoading(false);
      }
    };

    fetchProblems();
  }, [effectiveTopicId, problemId, assessment]);

  // Load answer when switching problems
  useEffect(() => {
    if (!currentProblem) return;

    const submission = currentProblem.submission_data?.[0];
    if (submission?.submission) {
      setAnswer(submission.submission);
    } else {
      setAnswer("");
    }
    setIsSaved(true);
  }, [currentProblemIndex, currentProblem]);

  // "Saved" only once there is a saved answer to this question.
  const hasSavedAnswer = Boolean(currentProblem?.submission_data?.[0]?.submission);
  // After the final answer the editor is read-only and the model answer shows.
  const isFinal = Boolean(currentProblem?.final);
  const [confirmingFinal, setConfirmingFinal] = useState(false);
  const [finalError, setFinalError] = useState("");
  const hasText = answer.replace(/<[^>]*>|&nbsp;/g, "").trim().length > 0;

  useEffect(() => {
    setConfirmingFinal(false);
    setFinalError("");
  }, [currentProblemIndex]);

  const handleAnswerChange = useCallback((value) => {
    setAnswer(value);
    setIsSaved(false);
  }, []);

  const handleSubmitFinal = async () => {
    if (!currentProblem || !effectiveTopicId || isSaving || !hasText) return;
    setIsSaving(true);
    setFinalError("");
    try {
      const response = await api.post(`course/${effectiveTopicId}/s/${currentProblem.id}/submit/`, {
        submission: answer,
      });
      setIsSaved(true);
      setConfirmingFinal(false);
      setProblems(prev => prev.map((p, i) =>
        i === currentProblemIndex
          ? { ...p, submission_data: [{ submission: answer }], submitted: true, final: true,
              answer_key: response.data?.answer_key }
          : p
      ));
    } catch (err) {
      setFinalError(err.response?.data?.error || "Couldn't submit your answer. Try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    if (!currentProblem || !effectiveTopicId || isSaving || isFinal) return;

    setIsSaving(true);
    try {
      await api.post(`course/${effectiveTopicId}/s/${currentProblem.id}/`, {
        submission: answer,
      });
      setIsSaved(true);

      // Update problems state
      setProblems(prev => prev.map((p, i) =>
        i === currentProblemIndex
          ? { ...p, submission_data: [{ submission: answer }] }
          : p
      ));

    } catch (err) {
      console.error("Failed to save:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePrevious = async () => {
    if (currentProblemIndex > 0) {
      if (!isSaved && !isFinal) await handleSave();
      setCurrentProblemIndex(prev => prev - 1);
    }
  };

  const handleNext = async () => {
    if (currentProblemIndex < problems.length - 1) {
      if (!isSaved && !isFinal) await handleSave();
      setCurrentProblemIndex(prev => prev + 1);
    }
  };

  const handleBack = () => {
    navigate(withAssessment(`/dashboard/workspace/${id}/assessment/${effectiveTopicId}`, assessment));
  };

  if (loading) {
    return <div className={styles.workspace}>Loading...</div>;
  }

  if (error) {
    return <div className={styles.workspace}>Error: {error}</div>;
  }

  if (!currentProblem) {
    return <div className={styles.workspace}>No questions found</div>;
  }

  return (
    <div className={styles.workspace}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <button className={styles.backButton} onClick={handleBack}>
            ←
          </button>
          <span className={styles.problemTitle}>
            {currentProblem.description?.slice(0, 50) || `Question ${currentProblemIndex + 1}`}
          </span>
          <span className={styles.subjectiveBadge}>SUBJECTIVE</span>
        </div>
        <div className={styles.headerRight}>
          <div className={styles.questionNav}>
            <button
              className={styles.questionNavBtn}
              onClick={handlePrevious}
              disabled={currentProblemIndex === 0}
            >
              ←
            </button>
            <span className={styles.questionNavText}>
              Question {currentProblemIndex + 1} of {problems.length}
            </span>
            <button
              className={styles.questionNavBtn}
              onClick={handleNext}
              disabled={currentProblemIndex === problems.length - 1}
            >
              →
            </button>
          </div>
        </div>
      </header>

      {/* Two-Pane Layout */}
      <div className={styles.subjectivePaneContainer}>
        {/* Question Pane */}
        <div className={styles.problemPane}>
          <div className={styles.paneTabs}>
            <div className={`${styles.paneTab} ${styles.paneTabActive}`}>
              Question
            </div>
          </div>
          <div className={styles.problemContent}>
            <div>
              <div className={styles.sectionLabel}>Question</div>
              <div className={styles.subjectiveQuestion}>
                <ReactMarkdown>{currentProblem.description}</ReactMarkdown>
              </div>
            </div>

            {currentProblem.max_score && (
              <div className={styles.testCaseInfo}>
                <div className={styles.testCaseRow}>
                  <span>Max score</span>
                  <span className={styles.testCaseValue}>{currentProblem.max_score}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Editor Pane */}
        <div className={styles.subjectiveEditorPane}>
          <div className={styles.subjectiveEditorHeader}>
            <span className={styles.editorLabel}>Your Answer</span>
            <span className={isFinal || (isSaved && hasSavedAnswer) ? styles.savedIndicator : styles.unsavedIndicator}>
              {isFinal ? "● Submitted · final" : !isSaved ? "○ Unsaved" : hasSavedAnswer ? "● Draft saved" : "○ Not answered yet"}
            </span>
          </div>

          <div className={styles.subjectiveEditorContent}>
            {isFinal ? (
              <div style={{ height: "100%", overflowY: "auto", padding: "12px 16px" }}>
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>{LOCKED_NOTE}</div>
                <div className="ql-editor" style={{ padding: 0 }} dangerouslySetInnerHTML={{ __html: answer }} />
                <AnswerReveal title="Model answer">
                  <ReferenceText text={currentProblem.answer_key?.model_answer} />
                </AnswerReveal>
              </div>
            ) : (
              <ReactQuill
                value={answer}
                onChange={handleAnswerChange}
                modules={modules}
                placeholder="Write your answer here..."
                style={{ height: "calc(100% - 42px)" }}
              />
            )}
          </div>

          <div className={styles.subjectiveEditorFooter}>
            <div className={styles.charCount}>
              {answer.replace(/<[^>]*>/g, "").length} characters
            </div>
            {!isFinal && (confirmingFinal ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}
                   data-testid="confirm-final">
                <span style={{ fontSize: 13, color: "#16232e" }}>
                  Submit as final? You can't change it afterwards, and the model answer will be shown.
                </span>
                <button className={styles.editorBtn} onClick={() => setConfirmingFinal(false)} disabled={isSaving}>Cancel</button>
                <button className={`${styles.editorBtn} ${styles.submitBtn}`} onClick={handleSubmitFinal} disabled={isSaving}>
                  {isSaving ? "Submitting..." : "Yes, submit"}
                </button>
              </div>
            ) : (
              <div style={{ display: "flex", gap: 8 }}>
                <button className={styles.editorBtn} onClick={handleSave} disabled={isSaving || isSaved}>
                  {isSaving ? "Saving..." : "Save draft"}
                </button>
                <button className={`${styles.editorBtn} ${styles.submitBtn}`} onClick={() => setConfirmingFinal(true)}
                        disabled={isSaving || !hasText}>
                  Submit final answer
                </button>
              </div>
            ))}
            {finalError && <span style={{ color: "#c93c3c", fontSize: 13 }}>{finalError}</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubjectiveWorkspace;
